import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Hint } from "react-autocomplete-hint";
import Fuse from "fuse.js";
import { debounce } from "lodash";
import { ICollaborator, ILevel1, ILevel2, ISearchProps, ITag } from "./interface";
import { Avatar, Divider, Tag, Tooltip } from "src/assets/imports/antdComponents";
import useOutsideClick from "./useOutsideClick";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/store";
import { setSearchText, setSelectedCollaborators, setSelectedStudy } from "src/pages/Studies/states/studiesSlice";
import { getInitials } from "src/pages/Studies/transformer";
import type { StudyTypeIconDescriptor } from "src/pages/Studies/transformer";
import StudyTypeIcon from "src/pages/Studies/components/StudyTypeIcon";
import { escapeRegExp } from "src/utils/security";
import { FeatureKeysType, ISelectedStudy, storyType, SupportedLanguageType } from "src/pages/Studies/models/interfaces";
import { useFeatures } from "src/config/featureManager";
import { setReportsSearchText, setReportsSelectedCollaborators } from "src/pages/Reports/states/reportsSlice";
import { ModuleType } from "../SubHeader/interface";
import Folder from "public/icon/Folder.svg";
import "./style.css";
import TagList from "./TagList";

const REPORTS_TYPE = storyType.type2;

const Searchbar: React.FC<ISearchProps> = ({ renderedFrom, payload, collaborators, onChange, onCollaboratorSearch, onClickSearch, onFolderClick }) => {
	const { isFeatureAvailable } = useFeatures();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const inputRef = useRef<HTMLInputElement>(null);
	const isStudyUpsert = isFeatureAvailable(FeatureKeysType.StudyUpsert);
	const isFolderEnabled = isFeatureAvailable(FeatureKeysType.EnableFolder);
	const allStudyTypes = useSelector((state: RootState) => state.studiesSlice.allStudyTypes) as
		| Array<{ label?: string; key?: string; icon2?: StudyTypeIconDescriptor | null }>
		| undefined;
	const getStudyTypeIcon = useCallback(
		(studyType?: string): StudyTypeIconDescriptor | null =>
			allStudyTypes?.find((type) => type?.label === studyType || type?.key === studyType)?.icon2 ?? null,
		[allStudyTypes],
	);

	const [input, setInput] = useState<string>("");
	// debouncedInput trails `input` by 300 ms — Fuse searches run against this.
	const [debouncedInput, setDebouncedInput] = useState<string>("");

	// Stable lodash-debounced setter; cancelled automatically on unmount.
	const debouncedSetInput = useRef(debounce((value: string) => setDebouncedInput(value), 300)).current;

	useEffect(() => {
		return () => debouncedSetInput.cancel(); // cancel pending call on unmount
	}, [debouncedSetInput]);

	const [suggestions, setSuggestions] = useState<ILevel1[]>([]);
	const [collaboratorSuggestions, setCollaboratorSuggestions] = useState<ICollaborator[]>([]);
	const [selectedTags, setSelectedTags] = useState<ITag[]>([]);
	const [hints, setHints] = useState<string[]>([]);
	const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);
	const [hoveredSuggestionId, setHoveredSuggestionId] = useState<string | null>(null);
	const [shouldPreventFocus, setShouldPreventFocus] = useState(false);

	const escapeRegex = (str: string) => str.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");

	const fuseOptions = useMemo(
		() => ({
			keys: ["level1Name", "lstLevel2s.level2Name", "folder.folder_name"],
			includeScore: true,
			threshold: 0.3,
		}),
		[],
	);

	const collaboratorFuseOptions = useMemo(
		() => ({
			keys: ["collaboratorName"],
			includeScore: true,
			threshold: 0.3,
		}),
		[],
	);

	const fuse = useMemo(() => new Fuse(payload, fuseOptions), [payload, fuseOptions]);
	const collaboratorFuse = useMemo(() => new Fuse(collaborators || [], collaboratorFuseOptions), [collaborators, collaboratorFuseOptions]);

	const getSuggestions = useCallback(
		(query: string): ILevel1[] => {
			const parts = query
				.toLowerCase()
				.split(";")
				.map((part) => part.trim());
			let matches: ILevel1[] = [];

			const resultSets = parts.map((part) => fuse.search(part).map(({ item }) => item));

			if (resultSets?.length > 0) {
				matches = resultSets.reduce((acc, resultSet) => {
					if (acc?.length === 0) return resultSet;
					return acc.filter((item) => resultSet.some((resultItem) => item.level1Name === resultItem.level1Name));
				}, []);
			}

			const folders = matches.filter((item) => item.isFolder === true);
			const studies = matches.filter((item) => item.isFolder !== true);

			return [...folders, ...studies].slice(0, 5);
		},
		[fuse],
	);

	const getCollaboratorSuggestions = useCallback(
		(query: string) => {
			const parts = query
				.toLowerCase()
				.split(";")
				.map((part) => part.trim());
			let matches: ICollaborator[] = [];

			const resultSets = parts.map((part) => collaboratorFuse.search(part).map(({ item }) => item));

			if (resultSets?.length > 0) {
				matches = resultSets.reduce((acc, resultSet) => {
					if (acc?.length === 0) return resultSet;
					return acc.filter((item) => resultSet.some((resultItem) => item.collaboratorName === resultItem.collaboratorName));
				}, []);
			}

			// Filter out duplicates
			const uniqueMatches = matches.filter((item, index, self) => index === self.findIndex((t) => t.collaboratorName === item.collaboratorName));

			return uniqueMatches.slice(0, 5);
		},
		[collaboratorFuse],
	);

	// -------------------------------------------------------------------------
	// All Fuse searches now react to `debouncedInput`, not raw `input`.
	// The input field itself stays snappy; expensive searches are throttled.
	// -------------------------------------------------------------------------
	useEffect(() => {
		if (debouncedInput) {
			const matches = getSuggestions(debouncedInput);
			const collaboratorMatches = getCollaboratorSuggestions(debouncedInput);
			setSuggestions(matches);
			setCollaboratorSuggestions(collaboratorMatches);
		} else {
			setSuggestions(payload.slice(0, 5));
			setCollaboratorSuggestions(collaborators ? collaborators.slice(0, 5) : []);
			setIsDropdownVisible(false);
		}
	}, [debouncedInput, getSuggestions, getCollaboratorSuggestions, payload, collaborators]);

	useEffect(() => {
		const hintList: string[] = [];
		payload?.forEach((level1) => {
			hintList.push(level1.level1Name);
			level1.lstLevel2s?.forEach((level2) => {
				hintList.push(level2.level2Name);
			});
		});
		if (collaborators) {
			collaborators?.forEach((collaborator) => {
				hintList.push(collaborator.collaboratorName);
			});
		}
		setHints(hintList);
	}, [payload, collaborators]);

	const handleCollaboratorClick = (collaborator: ICollaborator) => {
		const collaboratorTag: ITag = {
			collaboratorId: collaborator.collaboratorId,
			collaborator: collaborator.collaboratorName,
		};
		setSelectedTags((prevTags) => {
			const existingCollaborator = prevTags.find((tag) => tag.collaboratorId === collaborator.collaboratorId);
			if (!existingCollaborator) {
				const newTags = [...prevTags, collaboratorTag];
				onCollaboratorSearch && onCollaboratorSearch(newTags);
				onClickSearch && onClickSearch(input);
				return newTags;
			}
			return prevTags;
		});
		// Remove the last segment of the input and append the selected collaborator as a tag
		const parts = input.split(";");
		parts.pop(); // Remove the last segment
		const newInput = parts.join(";").trim();
		setInput(newInput ? `${newInput}; ` : ""); // Keep the rest of the input intact
		setCollaboratorSuggestions([]);
		setIsDropdownVisible(false);
	};

	// Handle folder click - navigate inside folder
	const handleFolderClick = useCallback(
		(suggestion: ILevel1) => {
			if (onFolderClick && suggestion.folderData) {
				onFolderClick(suggestion.folderData);
				resetSearchBox();
			}
		},
		[onFolderClick],
	);

	// Handle study click
	const handleSuggestionClick = useCallback(
		(suggestion: ILevel1) => {
			// If it's a folder, navigate to folder
			if (suggestion.isFolder) {
				handleFolderClick(suggestion);
				return;
			}
		},
		[handleFolderClick],
	);

	const removeTag = (id: string | undefined) => {
		const newTags = selectedTags.filter((tag) => tag.lvl1Id !== id && tag.collaboratorId !== id);
		setSelectedTags(newTags);
		onCollaboratorSearch && onCollaboratorSearch(newTags);
		onClickSearch && onClickSearch(input);
	};

	const onClickSearchIcon = () => {
		onClickSearch && onClickSearch(input);
		setIsDropdownVisible(false); // Collapse the dropdown after search
		inputRef.current?.blur(); // Make the input lose focus
	};

	// `showNoResults` compares against `debouncedInput` so the "No results"
	// message only appears once the debounce has settled — avoids a flash
	// of "No results" while the user is still mid-keystroke.
	const showNoResults = debouncedInput.trim().length > 0 && suggestions.length === 0 && collaboratorSuggestions.length === 0;

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			event.preventDefault();
			onClickSearch && onClickSearch(input);
			setIsDropdownVisible(false);
			setShouldPreventFocus(true);
			inputRef.current?.blur();
			setTimeout(() => {
				setShouldPreventFocus(false);
			}, 100);
		} else if (event.key === "Backspace" && input === "" && selectedTags?.length > 0) {
			// Remove the last tag when backspace is pressed and input is empty
			const lastTag = selectedTags[selectedTags?.length - 1];
			removeTag(lastTag.lvl1Id || lastTag.collaboratorId);
		}
	};

	const highlightMatch = useCallback((text: string, query: string): JSX.Element => {
		if (!query) {
			return <>{text}</>;
		}

		const parts = query
			.split(";")
			.map((part) => part.trim())
			.filter((part) => part !== "");
		const regex = new RegExp(`(${parts.map(escapeRegExp).join("|")})`, "gi");

		const segments = text.split(regex);

		return (
			<>
				{segments.map((segment, index) =>
					regex.test(segment) ? (
						<span
							key={index}
							className={`highlight whitespace-nowrap ${segment?.slice(0, 1) === " " ? "!ml-2" : ""} ${
								segments[index - 1]?.slice(segments[index - 1]?.length - 1) === " " ? "!ml-2" : ""
							}`}>
							{segment}
						</span>
					) : (
						<span
							key={index}
							className={`whitespace-nowrap ${segment?.slice(0, 1) === " " ? "!ml-2" : ""} ${
								segments[index - 1]?.slice(segments[index - 1]?.length - 1) === " " ? "!ml-2" : ""
							}`}>
							{segment}
						</span>
					),
				)}
			</>
		);
	}, []);

	const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>, suggestion: ILevel1, action: string) => {
		e.stopPropagation(); // Prevent triggering the suggestion click
		if (action === "insights") {
			handleViewInsights(suggestion);
		} else if (action === "manage") {
			handleManageStudies(suggestion);
		}
		resetSearchBox();
	};

	const resetSearchBox = () => {
		debouncedSetInput.cancel();
		setInput("");
		setDebouncedInput("");
		setSelectedTags([]);
		setSuggestions([]);
		setCollaboratorSuggestions([]);
		setIsDropdownVisible(false);
		setHoveredSuggestionId(null);
	};

	const handleManageStudies = (suggestion: ILevel1) => {
		const data: ISelectedStudy = {
			study_id: suggestion.level1Id,
			study_name: suggestion.level1Name,
			study_tags: suggestion.lstLevel2s?.map((tag) => tag.level2Name),
			study_collaborators: suggestion.collaborators,
			insights_id: suggestion.insights_id,
			access_type: suggestion.access_type,
			created_by: suggestion.created_by,
			study_type: suggestion.study_type,
			insights_available: suggestion.insightsAvailable,
			is_date_mapped: suggestion?.is_date_mapped ?? false,
			quant_tabs: suggestion?.quant_tabs,
			study_language: suggestion?.study_language ?? { name: "English", code: SupportedLanguageType.English },
			last_updated_at: suggestion?.last_updated_at,
			refresh_insights_id: suggestion?.refresh_insights_id,
		};
		dispatch(setSelectedStudy(data));
		navigate("/manage-studies");
	};

	const handleViewInsights = (suggestion: ILevel1) => {
		const data: ISelectedStudy = {
			study_id: suggestion.level1Id,
			study_name: suggestion.level1Name,
			study_tags: suggestion.lstLevel2s?.map((tag) => tag.level2Name),
			study_collaborators: suggestion.collaborators,
			insights_id: suggestion.insights_id,
			access_type: suggestion.access_type,
			created_by: suggestion.created_by,
			study_type: suggestion.study_type,
			insights_available: suggestion.insightsAvailable,
			is_date_mapped: suggestion?.is_date_mapped ?? false,
			quant_tabs: suggestion?.quant_tabs,
			study_language: suggestion?.study_language ?? { name: "English", code: SupportedLanguageType.English },
			last_updated_at: suggestion?.last_updated_at,
			refresh_insights_id: suggestion?.refresh_insights_id,
		};
		dispatch(setSelectedStudy(data));
		navigate("/insights");
	};

	const handleInputFocus = () => {
		if (shouldPreventFocus) return;
		if (input) {
			// Use current debouncedInput value; if it hasn't settled yet, fall back
			// to computing suggestions eagerly on focus so the dropdown isn't empty.
			const matches = getSuggestions(input);
			const collaboratorMatches = getCollaboratorSuggestions(input);
			setSuggestions(matches);
			setCollaboratorSuggestions(collaboratorMatches);
		} else {
			const initialSuggestions = payload.slice(0, 5);

			let initialCollaborators = collaborators ? collaborators.slice(0, 5) : [];

			// Filter out duplicates
			initialCollaborators = initialCollaborators.filter((item, index, self) => index === self.findIndex((t) => t.collaboratorName === item.collaboratorName));

			setSuggestions(initialSuggestions);
			setCollaboratorSuggestions(initialCollaborators);
		}
		setIsDropdownVisible(true);
	};

	const searchContainerRef = useOutsideClick(() => {
		setIsDropdownVisible(false);
	});

	// Add a useEffect hook to reset the search box on route change
	useEffect(() => {
		resetSearchBox();
		dispatch(setSelectedCollaborators([]));
		dispatch(setReportsSelectedCollaborators([]));
		dispatch(setSearchText());
		dispatch(setReportsSearchText());
	}, [location.pathname]);

	return (
		<div className="search-container" ref={searchContainerRef}>
			<div className="flex items-center input-container">
				<i
					className="material-symbols-outlined text-[1.4rem] cursor-pointer search-icon"
					tabIndex={0}
					role="button"
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							e.preventDefault();
							onClickSearchIcon();
						}
					}}
					onClick={onClickSearchIcon}>
					search
				</i>
				{selectedTags.map((tag, index) => (
					<Tag
						key={tag.lvl1Id ? `lvl1-${tag.lvl1Id}` : `collab-${tag.collaboratorId}`}
						className="tag"
						closable
						onClose={() => removeTag(tag.lvl1Id || tag.collaboratorId)}>
						{tag.collaborator ? <span className="com-tag mr-1">{tag.collaborator}</span> : <span className="com-tag mr-1">{tag.lvl1}</span>}
					</Tag>
				))}
				<div style={{ position: "relative", flex: 1, display: "flex", alignItems: "center" }}>
					<Hint options={hints} allowTabFill>
						<input
							tabIndex={-1}
							ref={inputRef}
							type="text"
							spellCheck="false"
							className="search-input"
							style={{ paddingRight: "1.5rem" }}
							placeholder={input || selectedTags?.length > 0 ? "" : t("Search by name, tags...")}
							value={input}
							onFocus={handleInputFocus}
							onChange={(e) => {
								const value = e.target.value;
								setInput(value); // updates immediately — keeps the field responsive
								debouncedSetInput(value); // schedules debouncedInput update after 300 ms
								e.target.setAttribute("data-value", value);
								onChange && onChange(value);
								if (!value) {
									debouncedSetInput.cancel(); // don't fire a stale search after clear
									setDebouncedInput("");
									resetSearchBox();
									dispatch(setSelectedCollaborators([]));
									dispatch(setReportsSelectedCollaborators([]));
									dispatch(setSearchText());
									dispatch(setReportsSearchText());
								} else {
									setIsDropdownVisible(true);
								}
							}}
							onKeyDown={handleKeyDown}
						/>
					</Hint>
					{(input || selectedTags?.length > 0) && (
						<i
							className="material-symbols-outlined text-[1.5rem] cursor-pointer text-[--neutral-grey] hover:text-[--secondary-color]"
							style={{ position: "absolute", right: "0.5rem" }}
							onClick={() => {
								resetSearchBox();
								dispatch(setSelectedCollaborators([]));
								dispatch(setReportsSelectedCollaborators([]));
								dispatch(setSearchText());
								dispatch(setReportsSearchText());
							}}>
							close
						</i>
					)}
				</div>
			</div>
			{isDropdownVisible && (showNoResults || suggestions?.length > 0 || collaboratorSuggestions?.length > 0) && (
				<div id="suggestions" className="suggestions-box">
					{showNoResults && (
						<div className="recent-searches">
							<div className="suggestion">
								<span className="suggestion-text flex items-center text-base-1 text-[--neutral-grey]">{t("No results found")}</span>
							</div>
						</div>
					)}
					{suggestions?.length > 0 && (
						<div className="suggested-searches">
							<div className="legend">
								<span className="suggestion-text flex items-center text-[--secondary-color] text-base-1 font-semibold">
									{renderedFrom === ModuleType.Studies ? t("Studies") : t("Reports")}
								</span>
								<span className="suggestion-tags">{/* <Tag className="legend-badge">{t("Custom Tags")}</Tag> */}</span>
							</div>
							{suggestions.map((suggestion, index) => {
								const studyTypeIcon = getStudyTypeIcon(suggestion.study_type);
								return (
								<div key={index} className="suggestion cursor-pointer" onClick={() => handleSuggestionClick(suggestion)}>
									<span className="suggestion-text flex items-center text-base-1 text-[--secondary-color]">
										{isFolderEnabled && suggestion.isFolder ? (
											<Folder className="mr-2" />
										) : studyTypeIcon ? (
											<StudyTypeIcon descriptor={studyTypeIcon} className="mr-2" />
										) : (
											<i className="material-symbols-outlined text-2xl text-[#D9D9D9] mr-2">local_library</i>
										)}
										{isFolderEnabled && suggestion.folder ? (
											<>
												<span className="text-[--neutral-grey] mr-1">{highlightMatch(suggestion.folder.folder_name, input)}</span>
												<span className="mx-1 mr-2 text-[--neutral-grey]">/</span>
												{highlightMatch(suggestion.level1Name, input)}
											</>
										) : (
											highlightMatch(suggestion.level1Name, input)
										)}
									</span>
									<span className="suggestion-tags">
										<TagList level2Tags={suggestion.lstLevel2s} query={input} highlightMatch={highlightMatch} />
									</span>
									<div className="suggestion-overlay"></div>
									<div className="suggestion-buttons">
										{renderedFrom === ModuleType.Studies &&
											!suggestion.isFolder &&
											(() => {
												const showInsights = suggestion.study_type !== REPORTS_TYPE;

												return (
													<>
														{showInsights && (
															<>
																<button
																	className="button"
																	disabled={!suggestion.insightsAvailable}
																	onClick={(e) => handleButtonClick(e, suggestion, "insights")}>
																	<i className="material-symbols-outlined">emoji_objects</i>
																</button>

																{isStudyUpsert && <Divider type="vertical" className="h-[1.33333rem] bg-[--secondary-color-2-opacity-15] mx-2" />}
															</>
														)}

														{isStudyUpsert && (
															<button className="button" onClick={(e) => handleButtonClick(e, suggestion, "manage")}>
																<i className="material-symbols-outlined">page_info</i>
															</button>
														)}
													</>
												);
											})()}
									</div>
								</div>
								);
							})}
						</div>
					)}
					{collaboratorSuggestions?.length > 0 && (
						<>
							<div className="recent-searches">
								<div className="legend-other">
									<span className="flex items-center text-[--secondary-color] text-base-1 font-semibold">{t("Collaborators")}</span>
								</div>
								{collaboratorSuggestions.map((collaborator, index) => (
									<div
										key={index}
										className="suggestion collab-suggestion"
										tabIndex={0}
										role="button"
										onKeyDown={(e) => {
											if (e.key === "Enter") {
												e.preventDefault();
												handleCollaboratorClick(collaborator);
											}
										}}
										onClick={() => handleCollaboratorClick(collaborator)}>
										<span className="suggestion-text flex items-center text-base-1 text-[--secondary-color]">
											<Avatar key={collaborator.collaboratorId} size="small" className="avatar mr-2">
												{getInitials(collaborator.collaboratorName)}
											</Avatar>
											{highlightMatch(collaborator.collaboratorName, input)}
										</span>
									</div>
								))}
							</div>
						</>
					)}
				</div>
			)}
		</div>
	);
};

export default Searchbar;
